import { useState, useCallback } from "react";
import apiConfig, { bookingAPIKey } from "../../../service/apiConfig";
import { useFormConfigContext } from "../../../common/contexts/FormConfigContext";
import isInboundFromNbly from "../../../utility/checkInBoundFromNbly";

interface PaymentApiRequest {
  conceptId: string;
  vendorId: string;
  CustomerNumber: number;
}

interface PaymentApiResponse {
  iframeUrl?: string;
  success: boolean;
  message?: string;
  [key: string]: any;
}

interface UsePaymentApiCallResult {
  loading: boolean;
  error: string | null;
  response: PaymentApiResponse | null;
  makePaymentRequest: (payload: PaymentApiRequest) => Promise<void>;
}

export const usePaymentApiCall = (): UsePaymentApiCallResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<PaymentApiResponse | null>(null);

  const { formConfig } = useFormConfigContext();

  const PAYMENT_API_ENDPOINT = apiConfig.realGreenApi;

  const handleMessage = useCallback((event: MessageEvent) => {
    if (!event?.data) return;
    const { response } = event.data;
    if (!response) return;
    globalThis.onbeforeunload = null;
    if (response?.Success === false && response?.Message === "IFrame Closed") {
      setError(response?.Message || "Payment failed");
      return;
    }
    if (response?.Model && response?.Success === true) {
      if (isInboundFromNbly()) {
        globalThis.location.replace(
          `${formConfig?.redirectionPageUrl}?leadconcept=${formConfig?.conceptCode}`,
        );
        return;
      }
      globalThis.location.replace(formConfig?.redirectionPageUrl);
    }
  }, []);

  window.addEventListener("message", handleMessage);

  const makePaymentRequest = useCallback(async (payload: PaymentApiRequest) => {
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      const response = await fetch(PAYMENT_API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apiKey: bookingAPIKey[process.env.NODE_ENV],
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PaymentApiResponse = await response.json();
      const { result } = data;

      if (result?.cardOnFile?.newCardOnFileUrl) {
        setResponse({
          iframeUrl: result.cardOnFile.newCardOnFileUrl,
          success: true,
        });
        return;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err?.message : "Unknown error occurred";
      console.error("Payment API call failed:", errorMessage);
      setError(null);
      setResponse(null);
      if (isInboundFromNbly()) {
        globalThis.location.replace(
          `${formConfig?.redirectionPageUrl}?leadconcept=${formConfig.conceptCode}`,
        );
        return;
      }
      globalThis.location.replace(
        formConfig?.redirectionPageUrl || "/confirmation",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    response,
    makePaymentRequest,
  };
};
